import React from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  PlayArrowIcon,
  Stack,
  Text,
  WorkIcon,
} from '@billda/design-system';
import {
  useTriggerPotStatusTransitions,
  useTriggerInvoiceGeneration,
  useProcessNotificationDefinitions,
  useProcessPushNotifications,
} from '../../hooks/useJobs';
import { UseMutationResult } from '@tanstack/react-query';
import { PermissionGate } from '../gates/PermissionGate';
import { Permissions } from '@billda/permissions-ui';

interface JobTriggerCardProps {
  title: string;
  description: string;
  mutation: UseMutationResult<{ message: string }, Error, void>;
}

const JobTriggerCard: React.FC<JobTriggerCardProps> = ({
  title,
  description,
  mutation,
}) => {
  // Auto-clear success/error after 5 seconds
  React.useEffect(() => {
    if (mutation.isSuccess || mutation.isError) {
      const timer = setTimeout(() => {
        mutation.reset();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [mutation.isSuccess, mutation.isError, mutation]);

  return (
    <Card>
      <CardContent className="p-6">
        <Text as="h6" variant="h6" className="mb-2">
          {title}
        </Text>
        <Text variant="body2" color="secondary" className="mb-4">
          {description}
        </Text>

        <div className="flex items-center gap-4">
          <Button
            variant="neutral"
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="min-w-[200px]"
          >
            {!mutation.isPending && (
              <PlayArrowIcon aria-hidden="true" className="size-4" />
            )}
            {mutation.isPending ? 'Running Job...' : 'Trigger Job'}
          </Button>

          {mutation.isSuccess && mutation.data && (
            <Alert severity="success" className="flex-1 py-0.5">
              {mutation.data.message}
            </Alert>
          )}

          {mutation.isError && (
            <Alert severity="error" className="flex-1 py-0.5">
              {mutation.error instanceof Error
                ? mutation.error.message
                : 'Failed to trigger job'}
            </Alert>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const JobsDebugSection: React.FC = () => {
  const potTransitionsMutation = useTriggerPotStatusTransitions();
  const invoiceGenerationMutation = useTriggerInvoiceGeneration();
  const notificationDefinitionsMutation = useProcessNotificationDefinitions();
  const pushNotificationsMutation = useProcessPushNotifications();

  return (
    <Stack spacing={3}>
      {/* Header */}
      <div>
        <div className="mb-1 flex items-center gap-2">
          <WorkIcon aria-hidden="true" className="size-5 text-content-accent" />
          <Text as="h6" variant="h6">
            Background Jobs
          </Text>
        </div>
        <Text variant="body2" color="secondary">
          Manually trigger background job processing for testing and development
        </Text>
      </div>

      <PermissionGate
        permission={Permissions.Developer.Jobs.PotTransition.Trigger}
        showRequiredPermissions={false}
      >
        <JobTriggerCard
          title="Pot Status Transitions"
          description="Processes all pots that need status transitions based on current time and deadlines. This job normally runs on a schedule but can be triggered manually here for testing."
          mutation={potTransitionsMutation}
        />
      </PermissionGate>

      <PermissionGate
        permission={Permissions.Developer.Jobs.InvoiceGeneration.Trigger}
        showRequiredPermissions={false}
      >
        <JobTriggerCard
          title="Invoice Generation"
          description="Generates invoices for all pots that are ready for invoicing. This job normally runs on a schedule but can be triggered manually here for testing."
          mutation={invoiceGenerationMutation}
        />
      </PermissionGate>

      <PermissionGate
        permission={Permissions.Developer.Jobs.NotificationDefinitions.Trigger}
        showRequiredPermissions={false}
      >
        <JobTriggerCard
          title="Process Notification Definitions"
          description="Expands pending notification definitions into per-device push notification records. Signals the definition processor background job to check for pending work."
          mutation={notificationDefinitionsMutation}
        />
      </PermissionGate>

      <PermissionGate
        permission={Permissions.Developer.Jobs.PushNotifications.Trigger}
        showRequiredPermissions={false}
      >
        <JobTriggerCard
          title="Dispatch Push Notifications"
          description="Sends pending push notifications to devices via the notification hub. Signals the push dispatcher background job to check for pending push notifications."
          mutation={pushNotificationsMutation}
        />
      </PermissionGate>
    </Stack>
  );
};
